import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useHeroImage } from '../hooks/usePageHero';
import Ticker from '../components/Ticker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Design tokens ─────────────────────────────────────────────────────────
const F_SERIF = "'Newsreader', Georgia, serif";
const F_SANS  = "'Public Sans', Inter, -apple-system, BlinkMacSystemFont, sans-serif";
const NAVY    = '#16304a';
const TEAL    = '#1d6f6a';
const GOLD    = '#c79a3a';
const BEIGE   = '#f6efe2';
const TEXT    = '#2a2e31';
const MUTED   = '#5b6066';
const BORDER  = '#e8e4db';
const CARD    = '#ffffff';

// ─── Helpers ────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return null;
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const SECTOR_MAP = {
  'education':       'Education',
  'healthcare':      'Healthcare',
  'business-finance':'Business & Finance',
  'government':      'Government',
  'technology-ai':   'Technology / AI',
  'workforce-labor': 'Workforce & Labor',
};
const sl = s => SECTOR_MAP[s] || (s || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Newsroom';

function Rule({ label, noLine }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: noLine ? 0 : 16 }}>
      <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ width: 18, height: 2, background: GOLD, flexShrink: 0 }} />
      {!noLine && <span style={{ flex: '1 1 40px', height: 1, background: '#e6e2d8' }} />}
    </div>
  );
}

function Skel({ style }) { return <div className="nr-skel" style={style} />; }

// ─── HERO — COMPLETELY LOCKED ───────────────────────────────────────────────
function HeroSection({ heroImg }) {
  const heroBg = heroImg ? `url(${heroImg}) center/cover no-repeat` : BEIGE;
  return (
    <div style={{ background: heroBg }} data-testid="nr-hero-section">
      <div className="container-custom">
        <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 36, alignItems: 'center', padding: '48px 0 52px' }} className="nr-hero-grid">
          <div>
            <h1 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 86, lineHeight: .94, margin: 0, color: NAVY, letterSpacing: '-1.5px' }} className="nr-h1" data-testid="newsroom-heading">
              Newsroom
            </h1>
            <div style={{ width: 56, height: 4, background: GOLD, margin: '16px 0 22px' }} />
            <p style={{ fontFamily: F_SERIF, fontSize: 23, lineHeight: 1.3, color: '#2d3b46', margin: '0 0 16px' }}>
              The daily editorial pulse of Opportunity Post.
            </p>
            <p style={{ fontFamily: F_SANS, fontSize: 15, lineHeight: 1.65, color: MUTED, maxWidth: 440, margin: '0 0 28px' }}>
              Independent reporting and smart analysis across the issues that move opportunity forward. Six sectors. Six days of fresh perspective. One destination.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/first-round" style={{ background: NAVY, color: '#fff', padding: '14px 22px', borderRadius: 4, fontWeight: 600, fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 12, fontFamily: F_SANS }} data-testid="nr-cta-briefing">
                Get First Round&nbsp;<span style={{ fontSize: 15 }}>→</span>
              </Link>
              <Link to="/magazine" style={{ background: 'transparent', border: `1px solid #b9ac8d`, color: NAVY, padding: '14px 22px', borderRadius: 4, fontWeight: 600, fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 12, fontFamily: F_SANS }} data-testid="nr-cta-magazine">
                Read Our Magazine&nbsp;<span style={{ fontSize: 15 }}>→</span>
              </Link>
            </div>
          </div>
          <div />
        </div>
      </div>
    </div>
  );
}

// ─── LEAD STORY ─────────────────────────────────────────────────────────────
// ─── PLACEHOLDER LEAD (shows 4 card shapes when no live articles in 7-day window) ──
const PLACEHOLDER_LEAD = [
  { id:'pld1', title:'AI Recruitment Tools May Introduce New Forms of Bias',        sector:'technology-ai',    dek:'New research raises questions about fairness and transparency as companies accelerate hiring automation across the entire pipeline.', slug:null, published_at:null, estimated_read_minutes:6, featured_image_url:null },
  { id:'pld2', title:'Community Health Workers Receive Federal Funding Boost',       sector:'healthcare',       dek:'HHS announces $250M allocation for frontline community health workers in underserved areas.',                                         slug:null, published_at:null, estimated_read_minutes:3, featured_image_url:null },
  { id:'pld3', title:'Small Business Loan Approvals Reach a Five-Year High',         sector:'business-finance', dek:'SBA data shows record approval rates, particularly for minority-owned businesses seeking under $150,000.',                              slug:null, published_at:null, estimated_read_minutes:4, featured_image_url:null },
  { id:'pld4', title:'Education Department Proposes New Teacher Pipeline Rules',     sector:'education',        dek:'Draft regulations would require states to track and publicly report on teacher shortage data annually.',                               slug:null, published_at:null, estimated_read_minutes:3, featured_image_url:null },
];

function LeadSkeleton() {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Skel style={{ width: 80, height: 11 }} /><Skel style={{ width: 18, height: 2 }} /><Skel style={{ flex: '1 1 40px', height: 1 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 18, alignItems: 'stretch' }} className="nr-leadzone-grid">
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1.05fr .95fr', background: CARD, minHeight: 300 }}>
          <div style={{ padding: '26px 24px 22px', display: 'flex', flexDirection: 'column' }}>
            <Skel style={{ width: 90, height: 20, borderRadius: 3, marginBottom: 16 }} /><Skel style={{ width: '92%', height: 28, marginBottom: 10 }} /><Skel style={{ width: '68%', height: 28, marginBottom: 18 }} /><Skel style={{ width: '100%', height: 13, marginBottom: 7 }} /><Skel style={{ width: '80%', height: 13 }} />
          </div>
          <Skel style={{ minHeight: 300, borderRadius: 0 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 16, display: 'grid', gridTemplateColumns: '1fr 58px', gap: 12, background: CARD, flex: 1, minHeight: 92 }}>
              <div><Skel style={{ width: 64, height: 10, marginBottom: 9 }} /><Skel style={{ width: '95%', height: 16, marginBottom: 6 }} /><Skel style={{ width: '55%', height: 12 }} /></div>
              <Skel style={{ width: 58, height: 58, borderRadius: 5 }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadCard({ article }) {
  const ago = timeAgo(article.published_at);
  const read = article.estimated_read_minutes > 0 ? `${article.estimated_read_minutes} min read` : null;
  return (
    <article style={{ border: `1px solid ${BORDER}`, borderRadius: 6, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1.05fr .95fr', background: CARD }} data-testid="nr-lead-card">
      <div style={{ padding: '26px 24px 22px', display: 'flex', flexDirection: 'column' }}>
        <span style={{ alignSelf: 'flex-start', color: TEAL, fontWeight: 700, fontSize: 10.5, letterSpacing: '1.2px', textTransform: 'uppercase', background: '#eaf3f1', padding: '5px 9px', borderRadius: 3 }}>{sl(article.sector)}</span>
        <h2 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 31, lineHeight: 1.12, margin: '16px 0 12px', color: NAVY }}>{article.title}</h2>
        {article.dek && <p style={{ fontSize: 14.5, lineHeight: 1.6, color: MUTED, margin: '0 0 20px', flex: '1 1 0' }}>{article.dek}</p>}
        <div style={{ marginTop: 14, fontSize: 12.5, color: '#9a9189' }}>{[article.author, ago, read].filter(Boolean).join(' · ')}</div>
        {article.slug && (
          <Link to={`/insights/${article.slug}`} style={{ marginTop: 14, color: NAVY, fontWeight: 600, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 7 }} data-testid="nr-lead-read-link">
            Read Story&nbsp;→
          </Link>
        )}
      </div>
      <div style={{ background: '#f3efe6', minHeight: 300, overflow: 'hidden' }}>
        {article.featured_image_url && <img src={article.featured_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />}
      </div>
    </article>
  );
}

function StackCard({ article, idx }) {
  const ago = timeAgo(article.published_at);
  const read = article.estimated_read_minutes > 0 ? `${article.estimated_read_minutes} min read` : null;
  const inner = (
    <article style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 16, display: 'grid', gridTemplateColumns: '1fr 58px', gap: 12, alignItems: 'center', background: CARD, height: '100%' }}>
      <div>
        <span style={{ display: 'block', color: TEAL, fontWeight: 700, fontSize: 10, letterSpacing: '1.1px', textTransform: 'uppercase', marginBottom: 7 }}>{sl(article.sector)}</span>
        <h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 17, lineHeight: 1.2, margin: '0 0 9px', color: NAVY }}>{article.title}</h3>
        <div style={{ fontSize: 11.5, color: '#a39c92' }}>{[ago, read].filter(Boolean).join(' · ')}</div>
      </div>
      <div style={{ width: 58, height: 58, border: `1px solid ${BORDER}`, borderRadius: 5, overflow: 'hidden', flexShrink: 0, background: '#faf8f3' }}>
        {article.featured_image_url && <img src={article.featured_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />}
      </div>
    </article>
  );
  if (article.slug) {
    return <Link to={`/insights/${article.slug}`} style={{ textDecoration: 'none', flex: 1 }} data-testid={`nr-stack-card-${idx}`}>{inner}</Link>;
  }
  return <div style={{ flex: 1 }} data-testid={`nr-stack-card-${idx}`}>{inner}</div>;
}

function LeadStory({ items }) {
  const data = (items && items.length > 0) ? items : PLACEHOLDER_LEAD;
  const [main, ...sides] = data;
  return (
    <section data-testid="nr-lead-story">
      <Rule label="Lead Story" />
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 18, alignItems: 'stretch' }} className="nr-leadzone-grid">
        <LeadCard article={main} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
          {sides.slice(0, 3).map((a, i) => <StackCard key={a.id || i} article={a} idx={i + 1} />)}
        </div>
      </div>
    </section>
  );
}

// ─── RIGHT RAIL ──────────────────────────────────────────────────────────────

function LatestHeadlines({ articles }) {
  if (!articles || articles.length === 0) return null;
  return (
    <section data-testid="nr-latest-headlines">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL }}>Latest Headlines</span>
        <span style={{ width: 18, height: 2, background: GOLD }} />
      </div>
      {articles.slice(0, 5).map((a, i) => (
        <Link key={a.id || i} to={`/insights/${a.slug}`}
          style={{ display: 'grid', gridTemplateColumns: '38px 1fr', gap: 10, padding: '12px 0', borderBottom: `1px solid #ececec`, textDecoration: 'none' }}
          data-testid={`nr-headline-${i}`}
        >
          <span style={{ fontSize: 11.5, color: '#a8a29a' }}>{timeAgo(a.published_at)}</span>
          <span style={{ fontSize: 14, lineHeight: 1.35, color: '#23272a' }}>{a.title}</span>
        </Link>
      ))}
      <Link to="/newsroom" style={{ display: 'block', textAlign: 'right', marginTop: 12, color: TEAL, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>
        View All Headlines&nbsp;→
      </Link>
    </section>
  );
}

// Most Read — CMS-ready; TODO: wire to view_count endpoint when available
function MostRead({ articles }) {
  const titles = articles && articles.length >= 3
    ? articles.slice(0, 3).map(a => ({ title: a.title, slug: a.slug }))
    : [
        { title: 'States Rewrite the Playbook on School Readiness', slug: null },
        { title: 'The Productivity Paradox in the AI Era', slug: null },
        { title: 'Why Infrastructure Still Matters Most', slug: null },
      ];
  return (
    <section style={{ background: '#f7f3ea', border: `1px solid #ece6d9`, borderRadius: 6, padding: '18px 18px 15px' }} data-testid="nr-most-read">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL }}>Most Read</span>
        <span style={{ width: 18, height: 2, background: GOLD }} />
      </div>
      {titles.map((item, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 11, alignItems: 'center', padding: '8px 0' }}>
          <span style={{ width: 24, height: 24, borderRadius: '50%', background: TEAL, color: '#fff', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
          {item.slug
            ? <Link to={`/insights/${item.slug}`} style={{ fontSize: 13.5, lineHeight: 1.3, color: '#23272a', textDecoration: 'none' }}>{item.title}</Link>
            : <span style={{ fontSize: 13.5, lineHeight: 1.3, color: '#23272a' }}>{item.title}</span>
          }
        </div>
      ))}
    </section>
  );
}

function BriefingWidget() {
  const [email, setEmail]       = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, products: ['opportunity_unlocked'], source_page: 'newsroom' });
    } catch {}
    setSubscribed(true);
    setLoading(false);
  };

  return (
    <section style={{ background: BEIGE, border: `1px solid #e7ddc9`, borderRadius: 6, padding: 22 }} data-testid="nr-briefing-widget">
      <div style={{ display: 'flex', gap: 13, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ width: 42, height: 42, borderRadius: '50%', background: '#efe3c9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 28 28" fill="none" stroke={NAVY} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="22" height="16" rx="2"/><path d="M3 8l11 8 11-8"/></svg>
        </span>
        <h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 20, lineHeight: 1.15, margin: 0, color: NAVY }}>Opportunity Unlocked</h3>
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: '#6c6258', margin: '0 0 14px' }}>Weekly Wisdom Delivered to Your Inbox</p>
      {subscribed ? (
        <div style={{ background: '#eaf3f1', border: `1px solid #cfe2dd`, borderRadius: 4, padding: '11px 13px', fontSize: 13, color: TEAL, fontWeight: 600 }}>
          ✓&nbsp;You're subscribed — watch your inbox.
        </div>
      ) : (
        <form onSubmit={handleSubscribe} style={{ display: 'flex', gap: 8 }} data-testid="nr-briefing-form">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" required
            style={{ flex: 1, minWidth: 0, border: `1px solid #d8cdb5`, borderRadius: 4, padding: '10px 12px', fontSize: 13, fontFamily: F_SANS, background: '#fff', color: TEXT, outline: 'none' }}
            data-testid="nr-briefing-email"
          />
          <button type="submit" disabled={loading}
            style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 4, padding: '10px 17px', fontWeight: 600, fontSize: 13, cursor: loading ? 'wait' : 'pointer', fontFamily: F_SANS }}
            data-testid="nr-briefing-subscribe"
          >Subscribe</button>
        </form>
      )}
      <p style={{ fontSize: 11, color: '#a89e8d', margin: '11px 0 0' }}>We respect your privacy.</p>
    </section>
  );
}

function RightRail({ articles, loading }) {
  return (
    <aside style={{ display: 'flex', flexDirection: 'column', gap: 28 }} data-testid="nr-right-rail">
      {loading
        ? <>{[...Array(5)].map((_, i) => <Skel key={i} style={{ height: 52, marginBottom: 4 }} />)}</>
        : <LatestHeadlines articles={articles} />
      }
      <MostRead articles={loading ? [] : articles} />
      <BriefingWidget />
    </aside>
  );
}

// ─── STORY FADER (3-card groups, 9 stories, auto-advance 60s) ──────────────
const PLACEHOLDER_FADER = [
  { id:'pf1', title:'States Rewrite School Readiness Standards',    sector:'education',        dek:'New frameworks emphasize early childhood development as a top state policy priority.',              slug:null, published_at:null, estimated_read_minutes:4, featured_image_url:null },
  { id:'pf2', title:'AI Tools Reshape the Modern Workplace',         sector:'technology-ai',    dek:'From scheduling to hiring, AI-powered tools are accelerating adoption across industries.',         slug:null, published_at:null, estimated_read_minutes:3, featured_image_url:null },
  { id:'pf3', title:'Federal Budget Signals Shift in Healthcare',    sector:'healthcare',       dek:'Proposed cuts and new allocations could reshape community health delivery for millions.',          slug:null, published_at:null, estimated_read_minutes:5, featured_image_url:null },
  { id:'pf4', title:'Business Leaders Divided on Remote Work',       sector:'business-finance', dek:'A new survey reveals deep splits across industries on return-to-office mandates.',                 slug:null, published_at:null, estimated_read_minutes:3, featured_image_url:null },
  { id:'pf5', title:'Labor Market Shows Signs of Cooling',           sector:'workforce-labor',  dek:'Job openings dipped last month while wages held steady across most sectors.',                      slug:null, published_at:null, estimated_read_minutes:4, featured_image_url:null },
  { id:'pf6', title:'Infrastructure Spending Reaches Rural Areas',   sector:'government',       dek:'Broadband and road projects are finally reaching long-underserved communities nationwide.',        slug:null, published_at:null, estimated_read_minutes:5, featured_image_url:null },
  { id:'pf7', title:'Community Colleges See Rising Enrollment',      sector:'education',        dek:'Applications are up 12% as students seek affordable pathways to stable employment.',              slug:null, published_at:null, estimated_read_minutes:3, featured_image_url:null },
  { id:'pf8', title:'Telehealth Growth Stabilizes Post-Pandemic',    sector:'healthcare',       dek:'After a rapid spike in adoption, virtual care is finding its steady operational state.',           slug:null, published_at:null, estimated_read_minutes:4, featured_image_url:null },
  { id:'pf9', title:'Automation Reshapes Manufacturing Workforce',   sector:'workforce-labor',  dek:'New data shows which roles are most at risk and which workers are gaining ground.',                slug:null, published_at:null, estimated_read_minutes:3, featured_image_url:null },
];

function FaderCard({ article, idx }) {
  const ago = timeAgo(article.published_at);
  const read = article.estimated_read_minutes > 0 ? `${article.estimated_read_minutes} min read` : null;
  const inner = (
    <article style={{ border:`1px solid ${BORDER}`, borderRadius:6, overflow:'hidden', background:CARD, height:'100%', display:'flex', flexDirection:'column' }}>
      <div style={{ height:142, background:'#edeae2', overflow:'hidden', flexShrink:0 }}>
        {article.featured_image_url && <img src={article.featured_image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} loading="lazy" />}
      </div>
      <div style={{ padding:'14px 16px 16px', display:'flex', flexDirection:'column', flex:1 }}>
        <span style={{ color:TEAL, fontWeight:700, fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', marginBottom:8, display:'block' }}>{sl(article.sector)}</span>
        <h3 style={{ fontFamily:F_SERIF, fontWeight:500, fontSize:16.5, lineHeight:1.2, margin:'0 0 9px', color:NAVY }}>{article.title}</h3>
        {article.dek && <p style={{ fontSize:13, lineHeight:1.5, color:MUTED, margin:'0 0 auto', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{article.dek}</p>}
        <div style={{ marginTop:12, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:11, color:'#b0a898' }}>{[ago, read].filter(Boolean).join(' · ')}</span>
          {article.slug && <span style={{ color:NAVY, fontWeight:600, fontSize:12.5, whiteSpace:'nowrap' }}>Read&nbsp;→</span>}
        </div>
      </div>
    </article>
  );
  if (article.slug) {
    return (
      <Link to={`/insights/${article.slug}`} style={{ textDecoration:'none', display:'flex', flexDirection:'column' }} data-testid={`nr-fader-card-${idx}`}>
        {inner}
      </Link>
    );
  }
  return <div style={{ display:'flex', flexDirection:'column' }} data-testid={`nr-fader-card-${idx}`}>{inner}</div>;
}

function StoryFader({ articles }) {
  const PAGES = 3;
  const pool = useMemo(() => {
    if (articles && articles.length >= 9) {
      return [...articles].sort(() => Math.random() - 0.5).slice(0, 9);
    }
    return PLACEHOLDER_FADER;
  }, [articles]);

  const [page, setPage]       = useState(0);
  const [visible, setVisible] = useState(true);

  const goTo = useCallback((getNext) => {
    setVisible(false);
    setTimeout(() => {
      setPage(prev => (typeof getNext === 'function' ? getNext(prev) : getNext));
      setVisible(true);
    }, 320);
  }, []);

  useEffect(() => {
    const id = setInterval(() => goTo(p => (p + 1) % PAGES), 60000);
    return () => clearInterval(id);
  }, [goTo]);

  const cards = pool.slice(page * 3, page * 3 + 3);

  return (
    <section style={{ marginTop:22 }} data-testid="nr-story-fader">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontWeight:700, fontSize:11, letterSpacing:'1.6px', textTransform:'uppercase', color:TEAL, whiteSpace:'nowrap' }}>More Stories</span>
          <span style={{ width:18, height:2, background:GOLD, flexShrink:0 }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ display:'flex', gap:5 }}>
            {[0,1,2].map(i => (
              <button key={i} onClick={() => goTo(i)} aria-label={`Page ${i+1}`}
                style={{ width:8, height:8, borderRadius:'50%', border:'none', cursor:'pointer', padding:0, background:i===page ? TEAL : '#d4cec5', transition:'background .2s' }}
              />
            ))}
          </div>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, opacity:visible ? 1 : 0, transition:'opacity .32s ease', alignItems:'stretch' }} className="nr-fader-grid">
        {cards.map((a, i) => <FaderCard key={a.id || a.slug || i} article={a} idx={i} />)}
      </div>
    </section>
  );
}

// ─── SECTOR NAV CARDS (6-col, replaces old SectorIndexBar + Big 6) ──────────
const SECTORS = [
  {
    label: 'Education', slug: '/coverage/education',
    icon: <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 9c-2.6-1.7-6-1.9-9-.7v15c3-1.2 6.4-1 9 .7 2.6-1.7 6-1.9 9-.7v-15c-3-1.2-6.4-1-9 .7z"/><path d="M16 9v15"/></svg>,
  },
  {
    label: 'Business & Finance', slug: '/coverage/business-finance',
    icon: <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 27V5"/><path d="M5 27h22"/><path d="M9 22l5-6 4 3 7-9"/><circle cx="9" cy="22" r="1.2" fill={TEAL}/><circle cx="25" cy="10" r="1.2" fill={TEAL}/></svg>,
  },
  {
    label: 'Government', slug: '/coverage/government',
    icon: <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke={TEAL} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 13L16 5l12 8"/><path d="M3 27h26M3 13h26"/><path d="M7 13v12M12 13v12M16 13v12M20 13v12M25 13v12"/></svg>,
  },
  {
    label: 'Healthcare', slug: '/coverage/healthcare',
    icon: <svg width="30" height="30" viewBox="0 0 32 32" fill="none" stroke={TEAL} strokeWidth="1.6" strokeLinejoin="round"><path d="M13 6h6v7h7v6h-7v7h-6v-7H6v-6h7z"/></svg>,
  },
  {
    label: 'Technology / AI', slug: '/coverage/technology-ai',
    icon: <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="16" cy="9" r="3"/><circle cx="9" cy="20" r="3"/><circle cx="23" cy="20" r="3"/><circle cx="16" cy="25" r="2.2"/><path d="M14.5 11.4l-3.4 6.2M17.5 11.4l3.4 6.2M11 22.4l3.2 1.8M21 22.4l-3.2 1.8"/></svg>,
  },
  {
    label: 'Workforce & Labor', slug: '/coverage/workforce-labor',
    icon: <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="16" cy="11" r="4"/><circle cx="8" cy="13" r="3"/><circle cx="24" cy="13" r="3"/><path d="M9 26c0-4 3.1-7 7-7s7 3 7 7"/><path d="M3 25c0-3 2-5 5-5M29 25c0-3-2-5-5-5"/></svg>,
  },
];

function SectorNavCards() {
  return (
    <section id="explore-by-sector" style={{ marginTop: 0 }} data-testid="nr-sector-nav">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 14 }} className="nr-six-grid">
        {SECTORS.map(s => (
          <Link
            key={s.slug}
            to={s.slug}
            style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '20px 12px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11, textDecoration: 'none', background: CARD, transition: 'border-color .15s, background .15s' }}
            data-testid={`sector-card-${s.slug.split('/').pop()}`}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#cfd9d4'; e.currentTarget.style.background = '#fbfcfb'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = CARD; }}
          >
            {s.icon}
            <h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 16, margin: 0, color: NAVY }}>{s.label}</h3>
            <p style={{ fontSize: 11.5, lineHeight: 1.45, color: '#8a8f93', margin: 0 }}>Fresh stories daily.<br/>Six-day shelf life.</p>
            <span style={{ color: NAVY, fontWeight: 600, fontSize: 12.5, marginTop: 2 }}>Explore&nbsp;→</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─── BOTTOM ROW (5-col) ──────────────────────────────────────────────────────

// Video of the Week — CMS-ready; TODO: wire to FeaturedVideo CMS content
function VideoOfWeek() {
  return (
    <div data-testid="nr-video-of-week">
      <div style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>Video of the Week</div>
      <div style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: `1px solid ${BORDER}`, cursor: 'pointer' }}>
        <svg viewBox="0 0 320 175" width="100%" style={{ display: 'block' }}>
          <rect width="320" height="175" fill="#dfeae6"/>
          <circle cx="86" cy="120" r="68" fill="#bcd2cb"/>
          <circle cx="200" cy="90" r="50" fill="#c8dcd7"/>
          <circle cx="160" cy="87.5" r="20" fill="rgba(0,0,0,.32)"/>
          <polygon points="154,78 154,97 174,87.5" fill="#fff"/>
        </svg>
        <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(0,0,0,.65)', color: '#fff', fontSize: 11.5, fontWeight: 600, padding: '3px 8px', borderRadius: 4 }}>6:24</div>
      </div>
      <h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 17, lineHeight: 1.2, margin: '12px 0 6px', color: NAVY }}>Building Opportunity in America's Heartland</h3>
      <p style={{ fontSize: 13, color: MUTED, margin: '0 0 10px', lineHeight: 1.4 }}>Leaders from business, education, and government on what's working—and what's next.</p>
      <span style={{ color: NAVY, fontWeight: 600, fontSize: 13 }}>Watch Now&nbsp;→</span>
    </div>
  );
}

// Opinion — live where available (editorial_slot: 'opinion'), placeholder fallback
const PLACEHOLDER_OPINION = [
  { title: "We Can't Afford to Ignore the Middle", author: 'Jonathan Reyes', date: 'May 20, 2025', slug: null },
  { title: 'The Case for Outcome-Driven Policymaking', author: 'Maya Patel', date: 'May 19, 2025', slug: null },
];

function OpinionSection({ editorial }) {
  const live = editorial ? editorial.filter(e => e.editorial_slot === 'opinion').slice(0, 2) : [];
  const items = live.length > 0
    ? live.map(e => ({ title: e.title, author: e.author || '', date: e.published_date || '', slug: e.url_slug }))
    : PLACEHOLDER_OPINION;

  return (
    <div data-testid="nr-opinion">
      <div style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>Opinion</div>
      {items.map((item, i) => (
        <div key={i} style={{ paddingBottom: 14, marginBottom: 14, borderBottom: i < items.length - 1 ? `1px solid #ececec` : 'none' }}>
          {item.slug
            ? <Link to={`/insights/${item.slug}`} style={{ textDecoration: 'none' }}><h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 16, lineHeight: 1.22, margin: '0 0 6px', color: NAVY }}>{item.title}</h3></Link>
            : <h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 16, lineHeight: 1.22, margin: '0 0 6px', color: NAVY }}>{item.title}</h3>
          }
          <div style={{ fontSize: 12, color: '#8a8f93' }}>By {item.author}{item.date ? `\u00a0·\u00a0${item.date}` : ''}</div>
        </div>
      ))}
      <Link to="/newsroom" style={{ color: TEAL, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>Read More&nbsp;→</Link>
    </div>
  );
}

// Editor's Corner — live where available (editorial_slot: 'editors_corner')
function EditorCorner({ editorial }) {
  const item = editorial ? editorial.find(e => e.editorial_slot === 'editors_corner') : null;
  return (
    <div data-testid="nr-editor-corner">
      <div style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>Editor's Corner</div>
      <h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 20, lineHeight: 1.15, margin: '0 0 10px', color: NAVY }}>
        {item ? item.title : "What We're Watching"}
      </h3>
      <p style={{ fontSize: 13, lineHeight: 1.55, color: MUTED, margin: '0 0 14px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
        {item?.summary || 'Our editors share the signals, stories, and trends shaping opportunity across the country.'}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e5dece', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M5 20c0-4 3.1-7 7-7s7 3 7 7"/></svg>
        </div>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>{item?.author || 'Marcus Bennett'}</div>
          <div style={{ fontSize: 11, color: '#8a8f93' }}>Editor-in-Chief</div>
        </div>
      </div>
      {item
        ? <Link to={`/insights/${item.url_slug}`} style={{ display: 'inline-block', marginTop: 12, color: NAVY, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>Read More&nbsp;→</Link>
        : <span style={{ display: 'inline-block', marginTop: 12, color: NAVY, fontWeight: 600, fontSize: 13 }}>Read More&nbsp;→</span>
      }
    </div>
  );
}

// Trending — CMS-ready; TODO: wire to view_count/trending endpoint
function TrendingSection({ articles }) {
  const items = articles && articles.length >= 5
    ? articles.slice(0, 5).map(a => ({ title: a.title, slug: a.slug }))
    : [
        { title: 'The AI Skills Gap Is Widening—Fast', slug: null },
        { title: 'States Bet Big on Career Pathways', slug: null },
        { title: 'Small Business Lending Finally Picks Up', slug: null },
        { title: 'The Future of Community Colleges', slug: null },
        { title: 'Data Privacy in the Age of AI', slug: null },
      ];
  return (
    <div data-testid="nr-trending">
      <div style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>Trending in the Newsroom</div>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 10, alignItems: 'start', padding: '7px 0' }}>
          <span style={{ width: 22, height: 22, borderRadius: '50%', background: TEAL, color: '#fff', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
          {item.slug
            ? <Link to={`/insights/${item.slug}`} style={{ fontSize: 13, lineHeight: 1.3, color: '#23272a', textDecoration: 'none' }}>{item.title}</Link>
            : <span style={{ fontSize: 13, lineHeight: 1.3, color: '#23272a' }}>{item.title}</span>
          }
        </div>
      ))}
      <Link to="/newsroom" style={{ display: 'inline-block', marginTop: 12, color: TEAL, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>View All Trends&nbsp;→</Link>
    </div>
  );
}

// Upcoming Events — CMS-ready; TODO: wire to conversations/events collection
const PLACEHOLDER_CONVERSATIONS = [
  { month: 'MAY', day: '27', time: '12:00 PM ET', title: 'The Future of Work: Skills, AI, and Human Potential' },
  { month: 'JUN', day: '03', time: '12:00 PM ET', title: 'Financing the Next Generation of Innovation' },
];

function UpcomingEvents() {
  return (
    <div data-testid="nr-upcoming-conversations">
      <div style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, marginBottom: 14 }}>Upcoming Events</div>
      {PLACEHOLDER_CONVERSATIONS.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: i < PLACEHOLDER_CONVERSATIONS.length - 1 ? 16 : 0, marginBottom: i < PLACEHOLDER_CONVERSATIONS.length - 1 ? 16 : 0, borderBottom: i < PLACEHOLDER_CONVERSATIONS.length - 1 ? `1px solid #ececec` : undefined }}>
          <div style={{ width: 50, borderRadius: 5, background: NAVY, color: '#fff', textAlign: 'center', padding: '8px 4px', flexShrink: 0 }}>
            <div style={{ fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', opacity: .85 }}>{c.month}</div>
            <div style={{ fontFamily: F_SERIF, fontSize: 21, lineHeight: 1.1 }}>{c.day}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: TEAL, fontWeight: 600, marginBottom: 3 }}>{c.time}</div>
            <h3 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 15, lineHeight: 1.15, margin: '0 0 6px', color: NAVY }}>{c.title}</h3>
            <Link to="/subscribe" style={{ color: NAVY, fontWeight: 600, fontSize: 12, textDecoration: 'none' }}>Register&nbsp;→</Link>
          </div>
        </div>
      ))}
      <Link to="/subscribe" style={{ display: 'inline-block', marginTop: 16, color: TEAL, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>View All Events&nbsp;→</Link>
    </div>
  );
}

function BottomRow({ editorial, articles }) {
  return (
    <section style={{ marginTop: 42, borderTop: `1px solid #e6e2d8`, paddingTop: 26, paddingBottom: 8 }} data-testid="nr-bottom-row">
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1.25fr 1.15fr', gap: 30 }} className="nr-bottom-grid">
        <VideoOfWeek />
        <OpinionSection editorial={editorial} />
        <EditorCorner editorial={editorial} />
        <TrendingSection articles={articles} />
        <UpcomingEvents />
      </div>
    </section>
  );
}

// ─── SECTOR ACTIVITY BAR ─────────────────────────────────────────────────────
// TODO: wire counts to backend sector story count endpoint
const SECTOR_ACTIVITY = [
  { label: 'Education',         count: 42, points: '0,22 12,18 24,24 36,10 48,16 60,8 72,12' },
  { label: 'Business & Finance', count: 38, points: '0,16 12,20 24,12 36,18 48,9 60,15 72,7' },
  { label: 'Government',        count: 31, points: '0,12 12,16 24,10 36,20 48,14 60,22 72,16' },
  { label: 'Healthcare',        count: 29, points: '0,20 12,14 24,18 36,12 48,18 60,10 72,14' },
  { label: 'Technology / AI',   count: 46, points: '0,22 12,16 24,18 36,9 48,13 60,6 72,9' },
  { label: 'Workforce & Labor', count: 33, points: '0,14 12,18 24,11 36,16 48,12 60,18 72,10' },
];

function SectorActivityBar() {
  const [showModal, setShowModal] = useState(false);
  return (
    <section style={{ marginTop: 36, borderTop: `1px solid #e6e2d8`, paddingTop: 24, paddingBottom: 8 }} data-testid="nr-sector-activity">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL }}>Sector Daily Traffic at a Glance</span>
        <button onClick={() => setShowModal(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEAL, fontWeight: 600, fontSize: 12.5, padding: 0, fontFamily: F_SANS }}>
          Full Activity Dashboard&nbsp;→
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 22 }} className="nr-sector-activity-grid">
        {SECTOR_ACTIVITY.map(s => (
          <div key={s.label}>
            <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 4 }}>{s.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: F_SERIF, fontSize: 34, lineHeight: 1, color: NAVY }}>{s.count}</span>
              <svg width="72" height="30" viewBox="0 0 72 30" fill="none" stroke={TEAL} strokeWidth="1.5" aria-hidden="true">
                <polyline points={s.points} />
              </svg>
            </div>
          </div>
        ))}
      </div>
      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position:'fixed', inset:0, background:'rgba(22,48,74,.45)', zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:8, padding:'36px 40px', maxWidth:380, width:'90%', textAlign:'center', boxShadow:'0 12px 40px rgba(0,0,0,.18)' }}>
            <div style={{ width:44, height:44, borderRadius:'50%', background:'#eaf3f1', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 18px' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>
            <h3 style={{ fontFamily:F_SERIF, fontWeight:500, fontSize:22, color:NAVY, margin:'0 0 10px' }}>Restricted Access</h3>
            <p style={{ fontSize:14, color:MUTED, lineHeight:1.65, margin:'0 0 24px' }}>
              The Full Activity Dashboard is available to<br /><strong>Administration and Advertising Partners</strong> only.
            </p>
            <button onClick={() => setShowModal(false)}
              style={{ background:NAVY, color:'#fff', border:'none', borderRadius:4, padding:'11px 28px', fontWeight:600, fontSize:13.5, cursor:'pointer', fontFamily:F_SANS }}>
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── FOOTER CTA BAND ─────────────────────────────────────────────────────────
function FooterCTABand() {
  return (
    <div style={{ background: BEIGE, marginTop: 44 }} data-testid="nr-footer-cta">
      <div className="container-custom">
        <div style={{ display: 'flex', alignItems: 'center', gap: 34, padding: '34px 0', flexWrap: 'wrap' }}>
          <h2 style={{ fontFamily: F_SERIF, fontWeight: 500, fontSize: 27, lineHeight: 1.15, margin: 0, color: NAVY, flexShrink: 0 }}>Better information.<br/>Stronger opportunity.</h2>
          <p style={{ flex: 1, minWidth: 220, color: MUTED, fontSize: 15, lineHeight: 1.5, margin: 0 }}>News that informs. Analysis that connects.<br/>A community that advances.</p>
          <Link to="/subscribe" style={{ background: NAVY, color: '#fff', padding: '14px 22px', borderRadius: 4, fontWeight: 600, fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 12, flexShrink: 0, fontFamily: F_SANS }}>
            Explore Opportunity Post&nbsp;<span style={{ fontSize: 15 }}>→</span>
          </Link>
          <svg width="66" height="66" viewBox="0 0 70 70" style={{ flexShrink: 0 }} aria-hidden="true">
            <g stroke={GOLD} strokeWidth="1.5" strokeLinecap="round">
              <line x1="49" y1="35" x2="65" y2="35"/><line x1="47.1" y1="42" x2="61" y2="50"/>
              <line x1="42" y1="47.1" x2="50" y2="61"/><line x1="35" y1="49" x2="35" y2="65"/>
              <line x1="28" y1="47.1" x2="20" y2="61"/><line x1="22.9" y1="42" x2="9" y2="50"/>
              <line x1="21" y1="35" x2="5" y2="35"/><line x1="22.9" y1="28" x2="9" y2="20"/>
              <line x1="28" y1="22.9" x2="20" y2="9"/><line x1="35" y1="21" x2="35" y2="5"/>
              <line x1="42" y1="22.9" x2="50" y2="9"/><line x1="47.1" y1="28" x2="61" y2="20"/>
            </g>
            <circle cx="35" cy="35" r="9" fill="none" stroke={GOLD} strokeWidth="1.5"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────
export default function NewsroomPage() {
  const heroImg = useHeroImage('newsroom');
  const [articles, setArticles]               = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [editorial, setEditorial]             = useState([]);

  useEffect(() => {
    axios.get(`${API}/newsroom/articles`)
      .then(r => setArticles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setArticles([]))
      .finally(() => setArticlesLoading(false));

    axios.get(`${API}/newsroom/editorial`)
      .then(r => setEditorial(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEditorial([]));
  }, []);

  const leadItems = articles.slice(0, 4);
  const railItems = articles.slice(4, 12);

  return (
    <div style={{ background: CARD, color: TEXT, fontFamily: F_SANS, lineHeight: 1.5, WebkitFontSmoothing: 'antialiased' }} data-testid="newsroom-page">

      <Ticker scope="all" />
      <HeroSection heroImg={heroImg} />

      <div className="container-custom" style={{ paddingTop: 40, paddingBottom: 0 }}>

        {/* Sector Nav Cards — right below hero */}
        <SectorNavCards />

        {/* 2-col: Lead Story + Right Rail */}
        <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr', gap: 38, alignItems: 'start', marginTop: 32 }} className="nr-main-grid">
          <div style={{ minWidth: 0 }}>
            {articlesLoading ? <LeadSkeleton /> : <LeadStory items={leadItems} />}
            <StoryFader articles={articles} />
          </div>
          <RightRail articles={railItems} loading={articlesLoading} />
        </div>

        {/* Bottom 5-col row */}
        <BottomRow editorial={editorial} articles={articles} />

        {/* Sector Activity at a Glance */}
        <SectorActivityBar />

      </div>

      {/* Footer CTA Band */}
      <FooterCTABand />

      <style>{`
        .nr-skel {
          background: linear-gradient(90deg, #eef1ee 25%, #f6f5f0 37%, #eef1ee 63%);
          background-size: 400% 100%;
          animation: nr-shimmer 1.5s ease infinite;
          border-radius: 4px;
        }
        @keyframes nr-shimmer {
          0%   { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @media (prefers-reduced-motion: reduce) { .nr-skel { animation: none; } }
        @media (max-width: 1080px) {
          .nr-hero-grid         { grid-template-columns: 1fr !important; }
          .nr-main-grid         { grid-template-columns: 1fr !important; }
          .nr-six-grid          { grid-template-columns: repeat(3,1fr) !important; }
          .nr-bottom-grid       { grid-template-columns: repeat(2,1fr) !important; }
          .nr-sector-activity-grid { grid-template-columns: repeat(3,1fr) !important; }
        }
        @media (max-width: 680px) {
          .nr-h1                { font-size: 52px !important; }
          .nr-hero-grid         { padding: 32px 0 36px !important; }
          .nr-leadzone-grid     { grid-template-columns: 1fr !important; }
          .nr-six-grid          { grid-template-columns: repeat(2,1fr) !important; }
          .nr-bottom-grid       { grid-template-columns: 1fr !important; }
          .nr-sector-activity-grid { grid-template-columns: repeat(2,1fr) !important; }
          .nr-fader-grid        { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 480px) {
          .nr-fader-grid        { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
